/**
 * One project tile (JARVIS-126): cover = the latest step with an image, the
 * category ("lane") badge, the `#N` priority badge, item count, last-edited
 * date and tags. The same component renders in the main grid and, smaller,
 * in the priority lane — `variant` picks the hover controls:
 *
 *   grid: ⚡ toggles lane membership (add / remove).
 *   lane: ◀ promote / ▶ demote (one place, via reorderProjectPriority) and ✕.
 *
 * Drag-and-drop is native HTML5. The tile only REPORTS drag/drop events; the
 * drop ZONE (grid tile, lane tile, lane, chip) decides which reorder runs.
 */
import {
  CloseOutlined,
  EditOutlined,
  LeftOutlined,
  RightOutlined,
  ThunderboltFilled,
  ThunderboltOutlined,
} from "@ant-design/icons"
import { Button, Tag, Tooltip, Typography } from "antd"
import {
  type DragEvent,
  type KeyboardEvent,
  memo,
  type MouseEvent,
  type ReactNode,
} from "react"
import styled from "styled-components"

import {
  getProjectCoverUrl,
  getProjectLastEditedMs,
} from "#components/PortfolioManagement/portfolioArrangement"
import {
  categoryColorMap,
  categoryLabelMap,
} from "#components/PortfolioManagement/portfolioPhotoGridConstants"
import { type PortfolioMockProject } from "#components/PortfolioManagement/portfolioUiTypes"
import { COLORS } from "#styles/colors"

import { formatLastEdited } from "./portfolioPageHelpers"

const { Text } = Typography

export type ProjectTileVariant = "grid" | "lane"
export type TileInsertSide = "before" | "after"

const MAX_VISIBLE_TAGS = 3

const STile = styled.div<{
  $variant: ProjectTileVariant
  $dragging: boolean
  $active: boolean
  $insert: TileInsertSide | null
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: ${({ $variant }) => ($variant === "lane" ? "150px" : "100%")};
  flex: 0 0 auto;
  border-radius: 10px;
  border: 1px solid ${({ $active }) => ($active ? "#91caff" : COLORS.GRAY3)};
  background: #fff;
  overflow: hidden;
  cursor: pointer;
  opacity: ${({ $dragging }) => ($dragging ? 0.45 : 1)};
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease,
    opacity 0.15s ease;
  box-shadow: ${({ $insert }) =>
    $insert === "before"
      ? "inset 3px 0 0 #1677ff"
      : $insert === "after"
        ? "inset -3px 0 0 #1677ff"
        : "none"};

  &:hover,
  &:focus-within {
    box-shadow: ${({ $insert }) =>
      $insert
        ? $insert === "before"
          ? "inset 3px 0 0 #1677ff, 0 6px 16px rgba(0, 0, 0, 0.12)"
          : "inset -3px 0 0 #1677ff, 0 6px 16px rgba(0, 0, 0, 0.12)"
        : "0 6px 16px rgba(0, 0, 0, 0.12)"};
  }

  &:hover [data-tile-controls],
  &:focus-within [data-tile-controls] {
    opacity: 1;
    pointer-events: auto;
  }

  &:focus-visible {
    outline: 2px solid #1677ff;
    outline-offset: 2px;
  }
`

const SCover = styled.div<{ $variant: ProjectTileVariant }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ $variant }) => ($variant === "lane" ? "4 / 3" : "1 / 1")};
  background: ${COLORS.GRAY1};
  overflow: hidden;
`

const SCoverImg = styled.img<{ $rotation: number }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transform: ${({ $rotation }) =>
    $rotation ? `rotate(${$rotation}deg)` : "none"};
`

const SNoImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.GRAY5};
  font-size: 12px;
`

const SPriorityBadge = styled.span`
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 22px;
  padding: 0 7px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  color: #ad6800;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 11px;
`

const SLaneBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  & .ant-tag {
    margin: 0;
  }
`

const SControls = styled.div`
  position: absolute;
  right: 6px;
  bottom: 6px;
  display: flex;
  gap: 4px;
  padding: 3px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s ease;
`

const SBody = styled.div<{ $variant: ProjectTileVariant }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: ${({ $variant }) =>
    $variant === "lane" ? "6px 8px 8px" : "8px 10px 10px"};
  min-width: 0;
`

const STags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
  & .ant-tag {
    margin: 0;
    font-size: 11px;
    line-height: 18px;
    padding: 0 5px;
  }
`

export interface PortfolioProjectTileProps {
  project: PortfolioMockProject
  variant: ProjectTileVariant
  /**
   * Tag text resolved from the dictionary by the page — the project only
   * stores ids, and an id the dictionary lost is simply absent here.
   */
  tagTexts?: readonly string[]
  /** 1-based lane position; renders the `#N` badge when set. */
  priorityIndex?: number
  coverRotation?: number
  isActive?: boolean
  isDragging?: boolean
  /** Insert indicator while another tile is dragged over this one. */
  insertSide?: TileInsertSide | null
  /** Unassigned is scanner-owned: never draggable, never in the lane. */
  draggable?: boolean
  onOpen: (projectId: string) => void
  onEdit?: (projectId: string) => void
  /** grid variant */
  onToggleLane?: (projectId: string) => void
  laneIsFull?: boolean
  /** lane variant */
  onPromote?: (projectId: string) => void
  onDemote?: (projectId: string) => void
  onRemoveFromLane?: (projectId: string) => void
  canPromote?: boolean
  canDemote?: boolean
  /** Drag source */
  onDragStart?: (event: DragEvent<HTMLDivElement>, projectId: string) => void
  onDragEnd?: () => void
  /** Drop zone (the tile itself). The zone owner decides what a drop means. */
  onDragOver?: (event: DragEvent<HTMLDivElement>, projectId: string) => void
  onDragLeave?: (projectId: string) => void
  onDrop?: (event: DragEvent<HTMLDivElement>, projectId: string) => void
}

const stop = (event: MouseEvent) => event.stopPropagation()

const ControlButton = ({
  title,
  icon,
  onClick,
  disabled,
  testId,
  danger,
}: {
  title: string
  icon: ReactNode
  onClick: () => void
  disabled?: boolean
  testId: string
  danger?: boolean
}) => (
  <Tooltip title={title}>
    <Button
      size="small"
      type="text"
      danger={danger}
      icon={icon}
      disabled={disabled}
      aria-label={title}
      data-testid={testId}
      onClick={(event) => {
        stop(event)
        onClick()
      }}
    />
  </Tooltip>
)

const EMPTY_TAG_TEXTS: readonly string[] = []

const PortfolioProjectTile = ({
  project,
  variant,
  tagTexts = EMPTY_TAG_TEXTS,
  priorityIndex,
  coverRotation = 0,
  isActive = false,
  isDragging = false,
  insertSide = null,
  draggable = true,
  onOpen,
  onEdit,
  onToggleLane,
  laneIsFull = false,
  onPromote,
  onDemote,
  onRemoveFromLane,
  canPromote = false,
  canDemote = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
}: PortfolioProjectTileProps) => {
  const coverUrl = getProjectCoverUrl(project)
  const inLane = priorityIndex !== undefined
  const itemCount = project.stages.length
  const lastEditedMs = getProjectLastEditedMs(project)
  const lastEdited = formatLastEdited(lastEditedMs)
  const visibleTags = tagTexts.slice(0, MAX_VISIBLE_TAGS)
  const hiddenTagCount = tagTexts.length - visibleTags.length

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return
    if (event.key !== "Enter" && event.key !== " ") return
    event.preventDefault()
    onOpen(project.id)
  }

  return (
    <STile
      $variant={variant}
      $dragging={isDragging}
      $active={isActive}
      $insert={insertSide}
      role="button"
      tabIndex={0}
      aria-label={`${project.title}, ${categoryLabelMap[project.category]}${
        inLane ? `, priority #${priorityIndex}` : ""
      }`}
      data-testid={
        variant === "lane" ? "portfolio-lane-tile" : "portfolio-tile"
      }
      data-project-id={project.id}
      data-category={project.category}
      data-priority={priorityIndex ?? undefined}
      data-last-edited={lastEditedMs}
      draggable={draggable}
      onClick={() => onOpen(project.id)}
      onKeyDown={handleKeyDown}
      onDragStart={(event) => {
        if (!draggable) {
          event.preventDefault()
          return
        }
        event.dataTransfer.effectAllowed = "move"
        event.dataTransfer.setData("text/plain", project.id)
        onDragStart?.(event, project.id)
      }}
      onDragEnd={onDragEnd}
      onDragOver={
        onDragOver ? (event) => onDragOver(event, project.id) : undefined
      }
      onDragLeave={onDragLeave ? () => onDragLeave(project.id) : undefined}
      onDrop={onDrop ? (event) => onDrop(event, project.id) : undefined}
    >
      <SCover $variant={variant}>
        {coverUrl ? (
          <SCoverImg
            src={coverUrl}
            alt={project.title}
            loading="lazy"
            decoding="async"
            draggable={false}
            $rotation={coverRotation}
          />
        ) : (
          <SNoImage>No image yet</SNoImage>
        )}
        {inLane ? (
          <Tooltip title={`Priority #${priorityIndex}`}>
            <SPriorityBadge
              aria-label={`Priority #${priorityIndex}`}
              data-testid="portfolio-priority-badge"
            >
              <ThunderboltFilled />#{priorityIndex}
            </SPriorityBadge>
          </Tooltip>
        ) : null}
        <SLaneBadge>
          <Tag color={categoryColorMap[project.category]}>
            {categoryLabelMap[project.category]}
          </Tag>
        </SLaneBadge>
        <SControls data-tile-controls onClick={stop}>
          {variant === "grid" &&
          onToggleLane &&
          project.category !== "unassigned" ? (
            <ControlButton
              title={
                inLane
                  ? "Remove from priority lane"
                  : laneIsFull
                    ? "Priority lane is full"
                    : "Add to priority lane"
              }
              icon={inLane ? <ThunderboltFilled /> : <ThunderboltOutlined />}
              disabled={!inLane && laneIsFull}
              onClick={() => onToggleLane(project.id)}
              testId="tile-lane-toggle"
            />
          ) : null}
          {variant === "lane" && onPromote ? (
            <ControlButton
              title="Promote (move earlier)"
              icon={<LeftOutlined />}
              disabled={!canPromote}
              onClick={() => onPromote(project.id)}
              testId="tile-promote"
            />
          ) : null}
          {variant === "lane" && onDemote ? (
            <ControlButton
              title="Demote (move later)"
              icon={<RightOutlined />}
              disabled={!canDemote}
              onClick={() => onDemote(project.id)}
              testId="tile-demote"
            />
          ) : null}
          {variant === "lane" && onRemoveFromLane ? (
            <ControlButton
              title="Remove from priority lane"
              icon={<CloseOutlined />}
              onClick={() => onRemoveFromLane(project.id)}
              testId="tile-lane-remove"
              danger
            />
          ) : null}
          {onEdit ? (
            <ControlButton
              title="Edit project"
              icon={<EditOutlined />}
              onClick={() => onEdit(project.id)}
              testId="tile-edit"
            />
          ) : null}
        </SControls>
      </SCover>
      <SBody $variant={variant}>
        <Text
          strong
          ellipsis={{ tooltip: project.title }}
          style={{ fontSize: variant === "lane" ? 12 : 14 }}
          data-testid="portfolio-tile-title"
        >
          {project.title}
        </Text>
        <Text
          type="secondary"
          style={{ fontSize: 11 }}
          data-testid="portfolio-tile-meta"
        >
          {itemCount} {itemCount === 1 ? "item" : "items"} · edited {lastEdited}
        </Text>
        {variant === "grid" && tagTexts.length > 0 ? (
          <STags data-testid="portfolio-tile-tags">
            {visibleTags.map((tag) => (
              <Tag key={tag}>{tag}</Tag>
            ))}
            {hiddenTagCount > 0 ? (
              <Tooltip title={tagTexts.slice(MAX_VISIBLE_TAGS).join(", ")}>
                <Tag>+{hiddenTagCount}</Tag>
              </Tooltip>
            ) : null}
          </STags>
        ) : null}
      </SBody>
    </STile>
  )
}

export default memo(PortfolioProjectTile)
